import {
  AGENT_EXCLUDED_SOURCE_IDS,
  AGENT_PROMPT_KEY_DESCRIBE,
  AGENT_PROMPT_KEY_FIND_SIMILAR,
  AGENT_PROMPT_KEY_IDENTIFY_STRUCTURES,
} from 'app/utils/constants';
import {parseLayerKey} from 'app/utils/layerUtils';
import {MapImageRef, MapImageRefs} from 'app/utils/mapUtils';

import {ApiExploreRun, ExploreRunAgentType} from '../types';

export const UNSUPPORTED_SCENES_MESSAGE =
  'Select 1-2 Truecolor (High Resolution) images on the map.';

export const EXCLUDED_SOURCE_MESSAGE =
  "The Agent can't run on this imagery source; please select another.";

const EXCLUDED_SOURCE_IDS: string[] = AGENT_EXCLUDED_SOURCE_IDS;

const refIsExcluded = (ref: MapImageRef) =>
  EXCLUDED_SOURCE_IDS.includes(parseLayerKey(ref.layerKey).sourceId);

export const imageRefsAreSupported = (imageRefs: MapImageRefs) =>
  imageRefs.length > 0 &&
  imageRefs.every(
    (ref) =>
      !!ref.cursor &&
      parseLayerKey(ref.layerKey).layerId === 'high-res-truecolor' &&
      !refIsExcluded(ref)
  );

export const getUnsupportedScenesMessage = (imageRefs: MapImageRefs) =>
  imageRefs.some(refIsExcluded) ? EXCLUDED_SOURCE_MESSAGE : UNSUPPORTED_SCENES_MESSAGE;

export interface AgentUseCase {
  // Tag the backend resolves to fixed prompt text (see PRESET_USER_PROMPTS).
  key: string;
  label: string;
  description: string;
  // Whether a run plan can fan this preset out over a portfolio. A preset without a batch path
  // shows as unavailable ("Coming soon") in the plan modal rather than being hidden.
  supportsBatch: boolean;
  // Compare-mode (two-scene) copy; falls back to label/description when omitted.
  compareLabel?: string;
  compareDescription?: string;
  // Set for a preset that only makes sense against a single image (e.g. drawing exemplars on
  // one scene). Compare mode disables the card and swaps its description to this message.
  compareDisabledMessage?: string;
  // Typewriter status phrases cycled through in RunView while a run is in
  // flight. First phrase doubles as the screen-reader label, so keep it stable.
  analysisPhrases: string[];
  // Compare-mode phrases; falls back to analysisPhrases when omitted.
  compareAnalysisPhrases?: string[];
}

// Fallback typewriter phrases for runs with no use-case-specific set (custom
// prompts, or an unrecognized key), one per mode.
export const DEFAULT_ANALYSIS_PHRASES = [
  'Inspecting imagery',
  'Scanning the scene',
  'Reviewing details',
  'Drafting notes',
];

export const DEFAULT_COMPARE_ANALYSIS_PHRASES = [
  'Inspecting imagery',
  'Comparing scenes',
  'Reviewing changes',
  'Drafting notes',
];

// Fixed prompts every user can run, resolved server-side by tag and scene count.
export const AGENT_USE_CASES: AgentUseCase[] = [
  {
    key: AGENT_PROMPT_KEY_DESCRIBE,
    label: "Describe what's present in this scene",
    description: 'A plain-language read of what the Agent sees',
    supportsBatch: true,
    compareLabel: "Describe what's changed between these scenes",
    compareDescription: 'Compare what changed between the two dates',
    analysisPhrases: [
      'Inspecting imagery',
      'Scanning the scene',
      'Identifying features',
      'Composing a description',
      'Drafting notes',
    ],
    compareAnalysisPhrases: [
      'Inspecting imagery',
      'Comparing scenes',
      'Detecting changes',
      'Summarizing differences',
      'Drafting notes',
    ],
  },
  {
    key: AGENT_PROMPT_KEY_IDENTIFY_STRUCTURES,
    label: 'Identify structures in this scene',
    description: 'Detects and outlines buildings and structures',
    supportsBatch: true,
    compareLabel: 'Identify structure changes between these scenes',
    compareDescription: 'Detects structures added, removed, or altered between the two dates',
    analysisPhrases: [
      'Inspecting imagery',
      'Scanning the scene',
      'Detecting structures',
      'Drafting notes',
    ],
    compareAnalysisPhrases: [
      'Inspecting imagery',
      'Comparing scenes',
      'Detecting structure changes',
      'Drafting notes',
    ],
  },
  {
    key: AGENT_PROMPT_KEY_FIND_SIMILAR,
    label: 'Find more like this',
    description:
      'Draw boxes around a few example objects and the agent finds every match on the property',
    // Single scene only: exemplar boxes are drawn on one image, not a before/after pair.
    supportsBatch: false,
    compareDisabledMessage: 'Draw examples on a single image',
    analysisPhrases: ['Studying your examples', 'Scanning the property', 'Refining matches'],
  },
];
export const AGENT_CUSTOM_PROMPT_KEY = 'custom';

export const AGENT_CUSTOM_PROMPT_USE_CASE: AgentUseCase = {
  key: AGENT_CUSTOM_PROMPT_KEY,
  label: 'Custom prompt [Internal Only]',
  description: 'Write your own prompt (Upstream only)',
  supportsBatch: true,
  analysisPhrases: DEFAULT_ANALYSIS_PHRASES,
  compareAnalysisPhrases: DEFAULT_COMPARE_ANALYSIS_PHRASES,
};

// Which engine a custom prompt drives, and how the box is labelled for each. The placeholder does
// the explaining: the two modes want very different text (a question vs. a concept list).
export const AGENT_TYPE_OPTIONS: {
  value: ExploreRunAgentType;
  label: string;
  placeholder: string;
  comparePlaceholder: string;
}[] = [
  {
    value: 'describe',
    label: 'Analyze (LLM)',
    placeholder: 'Ask a question about your property...',
    comparePlaceholder: 'Ask a question about the changes between the two scenes...',
  },
  {
    value: 'identify',
    label: 'Identify (segmentation)',
    placeholder:
      'List what to identify as a comma-separated list, e.g. solar panels, pools. Max 5 concepts.',
    comparePlaceholder:
      'List what to compare as a comma-separated list, e.g. solar panels, pools. Max 5 concepts.',
  },
];

export const getAgentTypePlaceholder = (agentType: ExploreRunAgentType, compareMode: boolean) => {
  const option = AGENT_TYPE_OPTIONS.find((o) => o.value === agentType)!;
  return compareMode ? option.comparePlaceholder : option.placeholder;
};

// Custom runs have no preset label, so name the engine that ran instead. samPrompt is set only by
// the segmentation agents, which is what tells the two apart.
export const getRunTitleFromKey = (
  promptKey: string | null,
  compareMode: boolean,
  samPrompt?: string | string[] | null
) => {
  const useCase = AGENT_USE_CASES.find((u) => u.key === promptKey);
  if (useCase) {
    return getUseCaseCopy(useCase, compareMode).label;
  }
  return samPrompt ? 'Custom (segmentation)' : 'Custom (llm)';
};

export const getRunTitle = (run: ApiExploreRun) => {
  if (run.config.prompt_key === AGENT_PROMPT_KEY_FIND_SIMILAR && run.config.sam_observation_label) {
    const count = run.config.sam_fewshot_exemplars?.length ?? 0;
    return `Find more like this · ${run.config.sam_observation_label} · ${count} example${
      count === 1 ? '' : 's'
    }`;
  }
  return getRunTitleFromKey(run.config.prompt_key, run.scenes.length === 2, run.config.sam_prompt);
};

// Typewriter phrases for a run's use case + mode, falling back to the default
// set for custom or unrecognized prompts.
export const getRunAnalysisPhrases = (run: ApiExploreRun): string[] => {
  const compareMode = run.scenes.length === 2;
  const useCase = AGENT_USE_CASES.find((u) => u.key === run.config.prompt_key);
  if (useCase) {
    return getUseCaseCopy(useCase, compareMode).phrases;
  }
  return compareMode ? DEFAULT_COMPARE_ANALYSIS_PHRASES : DEFAULT_ANALYSIS_PHRASES;
};

// Pick the card copy + typewriter phrases for the current mode, falling back to
// the single-scene values.
export function getUseCaseCopy(
  useCase: AgentUseCase,
  compareMode: boolean
): {label: string; description: string; phrases: string[]; disabledInCompare: boolean} {
  if (compareMode && useCase.compareDisabledMessage) {
    return {
      label: useCase.label,
      description: useCase.compareDisabledMessage,
      phrases: useCase.analysisPhrases,
      disabledInCompare: true,
    };
  }
  if (compareMode) {
    return {
      label: useCase.compareLabel ?? useCase.label,
      description: useCase.compareDescription ?? useCase.description,
      phrases: useCase.compareAnalysisPhrases ?? useCase.analysisPhrases,
      disabledInCompare: false,
    };
  }
  return {
    label: useCase.label,
    description: useCase.description,
    phrases: useCase.analysisPhrases,
    disabledInCompare: false,
  };
}
